// frontend/admin-portal/src/pages/ImportProducts.jsx
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Alert,
  Spinner,
  FileUploader,
} from "@fiberhome/shared-ui";
import { productService } from "../services/productService";
import CSVPreviewTable from "../components/products/CSVPreviewTable";
import ImportSummaryPanel from "../components/products/ImportSummaryPanel";

export default function ImportProducts() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [committed, setCommitted] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCommitted(null);
    setError("");
  };

  const handleTemplate = async () => {
    try {
      await productService.downloadTemplate();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleFiles = async (files) => {
    const f = files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setError("Please upload a .csv file");
      return;
    }
    setFile(f);
    setPreview(null);
    setCommitted(null);
    setError("");
    setBusy(true);
    try {
      const result = await productService.importPreview(f);
      setPreview(result);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Preview failed");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.previewRows?.length) return;
    setBusy(true);
    setError("");
    try {
      const summary = await productService.importConfirm(preview.previewRows);
      setCommitted(summary);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const canConfirm = preview?.summary?.valid > 0 && !committed && !busy;

  return (
    <>
      <PageHeader
        title="Import Products"
        subtitle="Bulk add or update products from a CSV file"
        breadcrumbs={["Home", "Products", "Import"]}
        actions={
          <div className="d-flex gap-2">
            <Button variant="ghost" onClick={reset} disabled={busy}>
              Reset
            </Button>
            <Button variant="outline" onClick={handleTemplate}>
              Download Template
            </Button>
            <Button variant="ghost" onClick={() => navigate("/products")}>
              ← Back to Products
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Step 1 — pick a file */}
      {!file && (
        <Card title="Step 1 — Upload your CSV">
          <p className="text-muted" style={{ fontSize: 13 }}>
            Expected columns: <code>sku</code>, <code>name</code>,{" "}
            <code>category</code>, <code>brand</code>, <code>price</code>,{" "}
            <code>costPrice</code>, <code>stock</code>, <code>minStock</code>,{" "}
            <code>unit</code>, <code>barcode</code>, <code>description</code>,{" "}
            <code>isActive</code>. Any column prefixed with{" "}
            <code>attributes.</code> becomes a spec field.
          </p>
          <FileUploader
            accept=".csv,text/csv"
            multiple={false}
            maxSizeMB={5}
            onFiles={handleFiles}
            label="Choose CSV file"
            dragLabel="Drag & drop your CSV here or click to browse"
          />
        </Card>
      )}

      {/* Step 2 — preview */}
      {file && busy && !preview && (
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
            <span className="ms-2">Parsing {file.name}…</span>
          </div>
        </Card>
      )}

      {preview && !committed && (
        <>
          <div className="mb-3">
            <Card title={`Step 2 — Review (${file.name})`}>
              <ImportSummaryPanel summary={preview.summary} dryRun />
              {preview.truncated && (
                <Alert variant="info" className="mt-3">
                  Showing first 500 rows out of {preview.totalRows} in the
                  preview table. All rows will still be imported when you
                  confirm.
                </Alert>
              )}
            </Card>
          </div>

          <div className="mb-3">
            <Card title="Row Details">
              <CSVPreviewTable rows={preview.previewRows} />
            </Card>
          </div>

          <Card>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="ghost" onClick={reset} disabled={busy}>
                Discard & choose another file
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                disabled={!canConfirm}
                loading={busy}
              >
                Import {preview.summary.valid} valid row(s)
              </Button>
            </div>
          </Card>
        </>
      )}

      {/* Step 3 — summary after commit */}
      {committed && (
        <>
          <Alert variant="success" className="mb-3" title="Import complete">
            Processed {preview.summary.valid} valid row(s).
          </Alert>
          <ImportSummaryPanel
            summary={{
              toCreate: committed.created,
              toUpdate: committed.updated,
              toSkip: committed.skipped,
              errors: committed.errors?.length || 0,
              failed: committed.failed,
            }}
            errors={committed.errors}
          />
          <div className="d-flex gap-2 mt-3 justify-content-end">
            <Button variant="outline" onClick={() => navigate("/products")}>
              Go to Products
            </Button>
            <Button variant="primary" onClick={reset}>
              Import another file
            </Button>
          </div>
        </>
      )}
    </>
  );
}
